import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { theme } from '../../styles/theme';
import { Seo, faqJsonLd, breadcrumbJsonLd } from '../../lib/seo';
import { supabase } from '../../lib/supabase';

type LutronAsset = {
  id: string;
  line_slug: string;
  kind: string;
  url: string;
  caption: string | null;
  position: number;
  target_name: string | null;
};

export type LutronSystemContent = {
  slug: string;
  name: string;
  longName: string;
  tagline: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  intro: string;
  forWho: string[];
  highlights: { title: string; desc: string }[];
  products: string[];
  faq: { q: string; a: string }[];

  // === Nuevos campos visuales ===
  tier: 'entry' | 'mid' | 'cableado' | 'value' | 'commercial' | 'luxury';
  vibe: string; // Frase de "look and feel"
  accentColor: string; // Color hex que diferencia visualmente la línea
  iconicProducts?: { model: string; name: string; type: string; wireless?: boolean }[];
  keypads?: { name: string; description: string; buttons?: string }[];
  finishes?: { name: string; hex: string; family?: 'gloss' | 'satin' | 'metal' | 'glass' }[];
  applications?: string[];
  loadCompat?: string;
  lutronUrl?: string;
};

export function LutronSystem({ content }: { content: LutronSystemContent }) {
  const url = `https://nuled.mx/lutron/${content.slug}`;
  const [assets, setAssets] = useState<LutronAsset[]>([]);

  useEffect(() => {
    supabase
      .from('lutron_assets')
      .select('id, line_slug, kind, url, caption, position, target_name')
      .eq('line_slug', content.slug)
      .order('position')
      .then(({ data }) => setAssets((data as LutronAsset[]) ?? []));
  }, [content.slug]);

  const heroAsset = assets.find(a => a.kind === 'hero');
  const keypadAssets = assets.filter(a => a.kind === 'keypad' || a.kind === 'product');
  const lifestyleAssets = assets.filter(a => a.kind === 'lifestyle' || a.kind === 'ambient');
  const videoAsset = assets.find(a => a.kind === 'video');

  // Foto para keypad específico: primero busca por target_name, si no, fallback por posición
  const photoForKeypad = (keypadName: string, index: number): LutronAsset | undefined => {
    const byName = keypadAssets.find(a => a.target_name === keypadName);
    if (byName) return byName;
    // Fallback: foto sin target_name asignado, en orden
    const unassigned = keypadAssets.filter(a => !a.target_name);
    return unassigned[index];
  };

  return (
    <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans }}>
      <Seo
        title={content.metaTitle}
        description={content.metaDescription}
        keywords={content.keywords}
        canonical={url}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: `Lutron ${content.longName}`,
            brand: { '@type': 'Brand', name: 'Lutron' },
            description: content.intro,
            url,
            offers: {
              '@type': 'AggregateOffer',
              priceCurrency: 'MXN',
              availability: 'https://schema.org/InStock',
              seller: { '@type': 'Organization', name: 'NULED' },
            },
          },
          breadcrumbJsonLd([
            { name: 'Inicio', url: 'https://nuled.mx/' },
            { name: 'Lutron México', url: 'https://nuled.mx/lutron' },
            { name: content.longName, url },
          ]),
          faqJsonLd(content.faq),
        ]}
      />

      <Header />

      {/* HERO con accentColor de la línea + foto si existe */}
      <section style={{
        background: heroAsset
          ? `linear-gradient(135deg, rgba(20,20,20,0.88) 0%, rgba(45,72,79,0.72) 55%, ${content.accentColor}AA 100%), url(${heroAsset.url}) center 30%/cover no-repeat`
          : `linear-gradient(135deg, ${theme.brandDark} 0%, ${theme.brand} 65%, ${content.accentColor} 100%)`,
        color: theme.textOnBrand,
        padding: '64px 24px 56px',
        minHeight: heroAsset ? 480 : 360,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Bandera de tier en esquina */}
        <div style={{
          position: 'absolute', top: 24, right: 24,
          fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700,
          padding: '4px 10px',
          background: 'rgba(255,255,255,0.12)',
          color: content.accentColor,
          border: `1px solid ${content.accentColor}`,
        }}>
          Tier {content.tier}
        </div>

        <div style={{ maxWidth: theme.maxWidth, margin: '0 auto' }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 12 }}>
            <Link to="/" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>Inicio</Link>
            {' / '}
            <Link to="/lutron" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>Lutron México</Link>
            {' / '}
            <span style={{ color: theme.textOnBrand }}>{content.longName}</span>
          </div>
          <div style={{
            fontSize: 12,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: content.accentColor,
            fontWeight: 700,
          }}>
            Sistema Lutron · Distribuidor mayorista autorizado
          </div>
          <h1 style={{
            fontFamily: theme.font.head,
            fontSize: 'clamp(36px, 5vw, 52px)',
            fontWeight: 300,
            lineHeight: 1.1,
            margin: '12px 0 16px',
            letterSpacing: -0.5,
          }}>
            Lutron <span style={{ fontWeight: 600 }}>{content.longName}</span> en México
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', maxWidth: 720 }}>
            {content.tagline}
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
            <Link
              to={`/catalogo?brand=lutron&line=${content.slug}`}
              style={{
                background: theme.accent,
                color: theme.brand,
                padding: '14px 28px',
                fontSize: 14,
                fontWeight: 700,
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              Ver productos {content.name} →
            </Link>
            <a
              href={`https://wa.me/525549332582?text=Hola%20NULED,%20quiero%20cotizar%20Lutron%20${encodeURIComponent(content.name)}`}
              style={{
                background: 'transparent',
                color: theme.textOnBrand,
                padding: '14px 28px',
                fontSize: 14,
                fontWeight: 500,
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                border: '1px solid rgba(255,255,255,0.4)',
              }}
            >
              Cotizar por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Vibe quote */}
      <section style={{
        background: theme.bgFooter,
        borderBottom: `1px solid ${theme.border}`,
        padding: '36px 24px',
      }}>
        <div style={{
          maxWidth: 920, margin: '0 auto',
          display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap',
        }}>
          <div style={{
            width: 6, alignSelf: 'stretch', minHeight: 60,
            background: content.accentColor,
          }} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>
              Look and feel
            </div>
            <p style={{
              fontFamily: theme.font.head, fontSize: 22, lineHeight: 1.4,
              color: theme.brand, margin: 0, fontWeight: 400, fontStyle: 'italic',
            }}>
              {content.vibe}
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section style={{ maxWidth: 920, margin: '0 auto', padding: '48px 24px' }}>
        <p style={{ fontSize: 17, lineHeight: 1.7, color: theme.text, margin: 0 }}>
          {content.intro}
        </p>
      </section>

      {/* Keypads icónicos · cada card puede tener foto */}
      {content.keypads && content.keypads.length > 0 && (
        <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '48px 24px' }}>
          <div style={eyebrowInline}>Keypads y controles</div>
          <h2 style={h2}>Las botoneras de {content.name}</h2>
          <p style={{ fontSize: 15, color: theme.textMuted, lineHeight: 1.7, maxWidth: 720, marginTop: 8 }}>
            Lutron es reconocido por la calidad de sus keypads — desde el paddle Caséta hasta los Palladiom de latón.
          </p>
          <div style={{
            marginTop: 28,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 20,
          }}>
            {content.keypads.map((k, i) => {
              const photo = photoForKeypad(k.name, i);
              return (
                <div key={k.name} style={{
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderTop: `4px solid ${content.accentColor}`,
                  display: 'flex', flexDirection: 'column',
                }}>
                  {photo && (
                    <div style={{
                      aspectRatio: '1 / 1',
                      backgroundColor: theme.bgImage,
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      <img
                        src={photo.url}
                        alt={k.name}
                        loading="lazy"
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          objectPosition: 'center',
                          padding: '8%',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}
                  <div style={{ padding: 20, flex: 1 }}>
                    <div style={{
                      fontFamily: theme.font.head, fontSize: 18, fontWeight: 600,
                      color: theme.brand, marginBottom: 6,
                    }}>
                      {k.name}
                    </div>
                    {k.buttons && (
                      <div style={{
                        fontSize: 11, color: content.accentColor,
                        fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6,
                        marginBottom: 10,
                      }}>
                        {k.buttons}
                      </div>
                    )}
                    <div style={{ fontSize: 13, color: theme.textMuted, lineHeight: 1.6 }}>
                      {k.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Galería lifestyle / ambient · solo si hay assets */}
      {lifestyleAssets.length > 0 && (
        <section style={{ background: theme.brand, color: theme.textOnBrand, padding: '64px 24px' }}>
          <div style={{ maxWidth: theme.maxWidth, margin: '0 auto' }}>
            <div style={{ fontSize: 12, color: content.accentColor, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700 }}>
              En contexto
            </div>
            <h2 style={{
              fontFamily: theme.font.head, fontSize: 30, fontWeight: 400,
              margin: '8px 0 24px', lineHeight: 1.15, color: theme.textOnBrand,
            }}>
              {content.name} en proyectos reales
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 16,
            }}>
              {lifestyleAssets.map(a => (
                <figure key={a.id} style={{ margin: 0 }}>
                  <div style={{
                    aspectRatio: '4/3',
                    backgroundImage: `url(${a.url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                  }} />
                  {a.caption && (
                    <figcaption style={{
                      fontSize: 12, color: 'rgba(255,255,255,0.7)',
                      padding: '8px 0', lineHeight: 1.5,
                    }}>
                      {a.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Video embed */}
      {videoAsset && (
        <section style={{ background: theme.bgImage, padding: '48px 24px' }}>
          <div style={{ maxWidth: 980, margin: '0 auto' }}>
            <div style={eyebrowInline}>Demo</div>
            <h2 style={h2}>{content.name} en acción</h2>
            <video
              src={videoAsset.url}
              controls
              style={{
                width: '100%', marginTop: 16,
                aspectRatio: '16/9', objectFit: 'cover',
                background: '#000',
                border: `1px solid ${theme.border}`,
              }}
            />
            {videoAsset.caption && (
              <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8 }}>
                {videoAsset.caption}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Acabados — la sección clave visual de Lutron */}
      {content.finishes && content.finishes.length > 0 && (
        <section style={{ background: theme.bgImage, padding: '56px 24px' }}>
          <div style={{ maxWidth: theme.maxWidth, margin: '0 auto' }}>
            <div style={eyebrowInline}>Paleta de acabados</div>
            <h2 style={h2}>Disponibles en {content.finishes.length} acabados</h2>
            <p style={{ fontSize: 15, color: theme.textMuted, lineHeight: 1.7, maxWidth: 720, marginTop: 8 }}>
              Cada keypad/dimmer puede pedirse en cualquiera de estos acabados. El acabado se confirma al momento del pedido — todos los acabados tienen el mismo precio mayoreo.
            </p>
            <div style={{
              marginTop: 28,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 14,
            }}>
              {content.finishes.map(f => (
                <div key={f.name} style={{
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: 80,
                    background: f.hex,
                    borderBottom: `1px solid ${theme.border}`,
                    position: 'relative',
                  }}>
                    {f.family && (
                      <span style={{
                        position: 'absolute', top: 6, right: 6,
                        fontSize: 8, padding: '2px 5px',
                        background: 'rgba(255,255,255,0.85)',
                        color: theme.brand, fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.4,
                      }}>
                        {f.family}
                      </span>
                    )}
                  </div>
                  <div style={{
                    padding: '8px 10px',
                    fontSize: 12, color: theme.brand, fontWeight: 600,
                    textAlign: 'center',
                  }}>
                    {f.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Aplicaciones */}
      {content.applications && content.applications.length > 0 && (
        <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '48px 24px' }}>
          <div style={eyebrowInline}>Aplicaciones típicas</div>
          <h2 style={h2}>Dónde se usa {content.name}</h2>
          <div style={{
            marginTop: 24,
            display: 'flex', gap: 10, flexWrap: 'wrap',
          }}>
            {content.applications.map(app => (
              <span key={app} style={{
                padding: '10px 16px',
                background: theme.bgFooter,
                border: `1.5px solid ${content.accentColor}`,
                color: theme.brand,
                fontSize: 13,
                fontWeight: 600,
              }}>
                {app}
              </span>
            ))}
          </div>
          {content.loadCompat && (
            <div style={{
              marginTop: 24, padding: 16,
              background: theme.bgFooter, border: `1px solid ${theme.border}`,
              borderLeft: `4px solid ${content.accentColor}`,
              fontSize: 13, color: theme.textMuted, lineHeight: 1.6,
            }}>
              <strong style={{ color: theme.brand }}>Compatibilidad de carga: </strong>
              {content.loadCompat}
            </div>
          )}
        </section>
      )}

      {/* Para quién + Productos */}
      <section style={{ background: theme.bgFooter, padding: '48px 24px' }}>
        <div style={{
          maxWidth: theme.maxWidth,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 32,
        }}>
          <div>
            <div style={eyebrowInline}>Indicado para</div>
            <h2 style={h2}>¿Es {content.name} para tu proyecto?</h2>
            <ul style={{ margin: '20px 0 0', padding: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
              {content.forWho.map(item => (
                <li key={item} style={{
                  paddingLeft: 24,
                  position: 'relative',
                  fontSize: 15,
                  color: theme.text,
                  lineHeight: 1.6,
                }}>
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    top: 6,
                    width: 12,
                    height: 12,
                    background: theme.accent,
                  }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div style={eyebrowInline}>Productos clave</div>
            <h2 style={h2}>Qué incluye {content.name}</h2>
            <div style={{ marginTop: 20, display: 'grid', gap: 10 }}>
              {content.products.map(p => (
                <div key={p} style={{
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderLeft: `3px solid ${theme.accent}`,
                  padding: '12px 16px',
                  fontSize: 14,
                  color: theme.brand,
                }}>
                  {p}
                </div>
              ))}
            </div>
            <Link
              to={`/catalogo?brand=lutron&line=${content.slug}`}
              style={{
                display: 'inline-block',
                marginTop: 16,
                background: theme.brand,
                color: theme.textOnBrand,
                padding: '12px 20px',
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              Ver productos {content.name} en catálogo →
            </Link>
          </div>
        </div>
      </section>

      {/* Highlights técnicos */}
      <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '64px 24px' }}>
        <div style={eyebrowInline}>Características</div>
        <h2 style={h2}>Por qué {content.name}</h2>
        <div style={{
          marginTop: 32,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 24,
        }}>
          {content.highlights.map(h => (
            <div key={h.title} style={{ paddingTop: 16, borderTop: `2px solid ${theme.accent}` }}>
              <div style={{ fontFamily: theme.font.head, fontSize: 17, fontWeight: 600, color: theme.brand, marginBottom: 8 }}>
                {h.title}
              </div>
              <div style={{ fontSize: 14, color: theme.textMuted, lineHeight: 1.6 }}>
                {h.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA bridge a NULED */}
      <section style={{
        background: theme.brand,
        color: theme.textOnBrand,
        padding: '40px 24px',
      }}>
        <div style={{
          maxWidth: theme.maxWidth,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 12, color: theme.accent, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 700 }}>
              Cotización integrada
            </div>
            <h3 style={{
              fontFamily: theme.font.head,
              fontSize: 22,
              fontWeight: 400,
              margin: '6px 0 0',
            }}>
              {content.name} <span style={{ fontWeight: 600 }}>+ luminarias NULED</span> en una sola propuesta
            </h3>
          </div>
          <a
            href={`https://wa.me/525549332582?text=Hola%20NULED,%20quiero%20cotizar%20mi%20proyecto%20con%20Lutron%20${encodeURIComponent(content.name)}%20y%20luminarias%20NULED`}
            style={{
              background: theme.accent,
              color: theme.brand,
              padding: '14px 28px',
              fontSize: 14,
              fontWeight: 700,
              textDecoration: 'none',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            Iniciar proyecto
          </a>
        </div>
      </section>

      {/* Link a Lutron oficial */}
      {content.lutronUrl && (
        <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '24px 24px 0' }}>
          <a
            href={content.lutronUrl}
            target="_blank"
            rel="noopener"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              fontSize: 12, color: theme.textMuted,
              textDecoration: 'none', fontWeight: 500,
              padding: '8px 12px', border: `1px solid ${theme.border}`,
            }}
          >
            <span>↗</span> Ver {content.name} en Lutron.com
          </a>
        </section>
      )}

      {/* FAQ específico */}
      <section style={{ maxWidth: 920, margin: '0 auto', padding: '40px 24px 64px' }}>
        <div style={eyebrowInline}>Preguntas frecuentes</div>
        <h2 style={h2}>{content.name} · Dudas frecuentes</h2>
        <div style={{ marginTop: 32 }}>
          {content.faq.map(({ q, a }, i) => (
            <details key={i} style={{
              borderTop: i === 0 ? `1px solid ${theme.border}` : 'none',
              borderBottom: `1px solid ${theme.border}`,
            }}>
              <summary style={{
                cursor: 'pointer',
                padding: '18px 4px',
                fontSize: 15,
                fontWeight: 600,
                color: theme.brand,
                fontFamily: theme.font.head,
                listStyle: 'none',
                position: 'relative',
                paddingRight: 32,
              }}>
                {q}
                <span style={{ position: 'absolute', right: 4, top: 18, color: theme.accentDark }}>+</span>
              </summary>
              <div style={{ padding: '0 4px 18px', fontSize: 14, color: theme.textMuted, lineHeight: 1.7 }}>
                {a}
              </div>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

const eyebrowInline: React.CSSProperties = {
  fontSize: 12,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: theme.accentDark,
  fontWeight: 700,
};

const h2: React.CSSProperties = {
  fontFamily: theme.font.head,
  fontSize: 30,
  fontWeight: 400,
  color: theme.brand,
  margin: '8px 0 12px',
  lineHeight: 1.15,
};
